import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v2, ApiError, type LifecycleEvent } from '@/api/v2Client';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { Spinner } from '@/components/dashboard/Spinner';
import { formatDate, formatDateTime } from '@/lib/format';
import { StatusBadge } from '@/components/StatusBadge';

type SequenceRow = Awaited<ReturnType<typeof v2.submissions.getSequenceHistory>>[number];

// Leave unknown actions readable without inventing an interpretation of the event.
function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    'submission.update': 'Submission updated',
    'submission.create': 'Submission created',
    'document.lifecycle.change': 'Document lifecycle changed',
    'document.upload': 'Document uploaded',
    'document.delete': 'Document deleted',
    'document.confirm': 'Document classification confirmed',
  };
  return labels[action] ?? action.replace(/[._-]+/g, ' ');
}

export function LifecycleTabPanel({ submissionId }: { submissionId?: string }): ReactElement {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState<SequenceRow[] | null>(null);
  const [seqError, setSeqError] = useState<string | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditSequence, setAuditSequence] = useState(submissionId ?? '');
  const [events, setEvents] = useState<LifecycleEvent[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setAuditOpen(false);
    setAuditSequence(submissionId ?? '');
    setSequences(null);
    setSeqError(null);
    if (submissionId) void v2.submissions.getSequenceHistory(submissionId)
      .then((rows) => { if (current) setSequences(rows); })
      .catch((error: unknown) => {
        if (current) { setSeqError(error instanceof ApiError ? error.message : 'Failed to load sequence history'); setSequences([]); }
      });
    return () => { current = false; };
  }, [submissionId]);

  useEffect(() => {
    let current = true;
    setEvents(null);
    setEventsError(null);
    if (auditOpen && auditSequence) void v2.submissions.getLifecycleHistory(auditSequence)
      .then((result) => { if (current) setEvents([...result.events]); })
      .catch((error: unknown) => {
        if (current) { setEventsError(error instanceof ApiError ? error.message : 'Failed to load audit events'); setEvents([]); }
      });
    return () => { current = false; };
  }, [auditOpen, auditSequence]);

  if (!submissionId) return <EmptyState icon="📜" heading="Lifecycle History" subtext="Lifecycle history will appear here once this submission has been compiled and submitted." />;

  return <div className="lifecycle-history" style={{ padding: 16 }}>
    <div role="tablist" aria-label="Lifecycle views" className="lifecycle-view-tabs">
      {['Sequence History', 'Audit Events'].map((label, index) => <button key={label} type="button" role="tab" id={`lifecycle-view-${index}`} aria-controls={`lifecycle-panel-${index}`} aria-selected={auditOpen === Boolean(index)} tabIndex={auditOpen === Boolean(index) ? 0 : -1}
        onClick={() => setAuditOpen(Boolean(index))}
        onKeyDown={(event) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault(); const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
            setAuditOpen(Boolean(next)); document.getElementById(`lifecycle-view-${next}`)?.focus();
          }
        }}>{label}</button>)}
    </div>
    <div role="tabpanel" id="lifecycle-panel-0" aria-labelledby="lifecycle-view-0" hidden={auditOpen}>
    {seqError && <div role="alert">{seqError}</div>}
    {sequences === null ? <Spinner caption="Loading sequence history…" /> : sequences.length ? (
      <table className="sequence-history-table" data-testid="sequence-history-table">
        <thead><tr><th>Sequence</th><th>Description</th><th>Submission type</th><th>Status</th><th>Documents</th><th>Last updated</th></tr></thead>
        <tbody>{sequences.map((row) => {
          const url = `/submissions/${encodeURIComponent(row.Id)}?tab=lifecycle`;
          return <tr key={row.Id} data-testid={`seq-row-${row.Id}`} aria-current={row.Id === submissionId ? 'true' : undefined}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('a,button') || window.getSelection()?.toString()) return;
              void navigate(url);
            }}>
            <td data-label="Sequence"><Link to={url} aria-label={`Open sequence ${String(row.SequenceNumber).padStart(4, '0')}`}>{String(row.SequenceNumber).padStart(4, '0')}</Link>{row.Id === submissionId && <span className="sequence-current">Current</span>}</td>
            <td data-label="Description">{row.SequenceDescription || 'No description recorded'}</td>
            <td data-label="Submission type">{row.SubmissionType || 'Not recorded'}</td>
            <td data-label="Status"><StatusBadge status={row.Status} /></td>
            <td data-label="Documents"><span data-testid={`seq-doc-count-${row.Id}`}>{row.DocumentCount}</span><small style={{ display: 'block' }}><span data-testid={`seq-confirmed-count-${row.Id}`}>{row.ConfirmedCount}</span> confirmed</small></td>
            <td data-label="Last updated">{formatDate(row.UpdatedAt)}</td>
          </tr>;
        })}</tbody>
      </table>
    ) : !seqError && <p>No sequence history available yet.</p>}

    </div>
    <div role="tabpanel" id="lifecycle-panel-1" aria-labelledby="lifecycle-view-1" hidden={!auditOpen} className="lifecycle-audit">
      {auditOpen && <div style={{ padding: 12 }}>
        <p style={{ marginTop: 0, color: 'var(--text-muted)', fontSize: 12 }}>Latest 100 events for the selected sequence and its documents. Open technical details to inspect the recorded changes.</p>
        <label>Sequence <select aria-label="Audit sequence" value={auditSequence} onChange={(event) => setAuditSequence(event.target.value)} data-testid="seq-filter-select">
          {(sequences?.length ? sequences : [{ Id: submissionId, SequenceNumber: '' }]).map((row) => <option key={row.Id} value={row.Id}>{row.SequenceNumber === '' ? 'Current sequence' : `Sequence ${String(row.SequenceNumber).padStart(4, '0')}`}{row.Id === submissionId ? ' (current)' : ''}</option>)}
        </select></label>
        {eventsError ? <div role="alert">{eventsError}</div> : events === null ? <Spinner caption="Loading events…" /> : events.length === 0 ? <p>No events recorded for this sequence.</p> : (
          <ol className="lifecycle-event-list" data-testid="lifecycle-events-table">{events.map((event) => <li key={event.id} data-testid={`event-row-${event.id}`}>
            <strong>{actionLabel(event.action)}</strong>
            <div>{event.userName || event.userId || 'User not recorded'} · <time dateTime={event.timestamp}>{formatDateTime(event.timestamp)}</time></div>
            <details><summary>Technical details</summary><dl><dt>Action</dt><dd>{event.action}</dd><dt>Target</dt><dd>{event.targetType}: {event.targetId}</dd><dt>Event ID</dt><dd>{event.id}</dd><dt>Recorded changes</dt><dd><pre>{JSON.stringify({ before: event.before, after: event.after }, null, 2)}</pre></dd></dl></details>
          </li>)}</ol>
        )}
      </div>}
    </div>
  </div>;
}
